import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Headphones, Mountain } from "lucide-react";
import { TRAILS, IMAGES } from "../data/content";
import { WaveformDecor } from "../components/Waveform";
import { FinalCTA } from "./Home";

export default function FeaturedTrails() {
  return (
    <div data-testid="page-featured-trails">
      {/* PAGE HERO */}
      <section className="relative pt-40 pb-20 md:pt-52 md:pb-28 bg-brand-ink text-brand-cream overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: `url(${IMAGES.rainforestPath})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/60 to-brand-ink/95" />
        <WaveformDecor className="absolute bottom-6 left-0 right-0 h-20 opacity-30" color="#C06E52" opacity={0.6} />
        <div className="container-px relative z-10">
          <div className="overline text-brand-ochre">Featured Trails</div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.04] max-w-4xl">
            Discover places <span className="italic font-light">through story.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-brand-cream/75 text-lg font-light leading-relaxed">
            Each Talking Trails Experience is shaped by the people, landscapes and stories that
            make a place unique. Walk slowly. Listen carefully. Linger.
          </p>
        </div>
      </section>

      {/* TRAILS */}
      <section className="py-20 md:py-28">
        <div className="container-px space-y-20 md:space-y-32">
          {Object.values(TRAILS).map((t, idx) => (
            <article
              key={t.slug}
              data-testid={`trail-card-${t.slug}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center ${
                idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <Link
                to={`/trails/${t.slug}`}
                className="relative lg:col-span-7 block aspect-[4/3] md:aspect-[16/10] overflow-hidden group"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  style={{ backgroundImage: `url(${t.hero})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
                <div className="absolute top-6 left-6 flex items-center gap-2 text-brand-cream">
                  <span className="w-2 h-2 rounded-full bg-brand-ochre animate-pulse-dot" />
                  <span className="overline text-brand-cream/90">{t.region}</span>
                </div>
                {t.badge && (
                  <img
                    src={t.badge}
                    alt={`${t.shortName || t.name} badge`}
                    className="absolute top-6 right-6 w-24 md:w-28 drop-shadow-2xl rotate-[6deg]"
                  />
                )}
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-brand-cream">
                  <div className="text-xs uppercase tracking-[0.22em]">{String(idx + 1).padStart(2, "0")} / 0{Object.values(TRAILS).length}</div>
                  <div className="text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2 opacity-80 group-hover:opacity-100">
                    Open trail <ArrowRight size={14} />
                  </div>
                </div>
              </Link>

              <div className="lg:col-span-5">
                <div className="overline">{t.stops} stops · {t.duration}</div>
                <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-brand-charcoal">
                  {t.name}
                </h2>
                <p className="mt-5 text-brand-muted text-lg font-light leading-relaxed">
                  {t.intro}
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {t.themes.map((th) => (
                    <span
                      key={th}
                      className="text-[11px] uppercase tracking-[0.18em] border border-brand-charcoal/20 px-3 py-1.5 text-brand-charcoal/80"
                    >
                      {th}
                    </span>
                  ))}
                </div>

                <div className="mt-7 flex items-center gap-6 text-sm text-brand-muted">
                  <span className="flex items-center gap-2"><Headphones size={14} className="text-brand-ochre" /> {t.stops} listening stops</span>
                  <span className="flex items-center gap-2"><Mountain size={14} className="text-brand-ochre" /> {t.duration}</span>
                </div>                <Link
                  to={`/trails/${t.slug}`}
                  data-testid={`trail-explore-${t.slug}`}
                  className="mt-8 inline-flex items-center gap-2 bg-brand-rainforest text-brand-cream px-7 py-3.5 text-xs uppercase tracking-[0.22em] hover:bg-brand-rainforestDark transition-colors"
                >
                  Explore {t.shortName || t.name.split(" ")[0]} <ArrowRight size={14} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
